/**
 * Las secciones del mostrador.
 *
 * POR QUE ESTO NO VIVE ADENTRO DE /catalogo-interno
 * ------------------------------------------
 * Vivio ahi mucho tiempo, y el precio fue que Catalogo e Inventario terminaron
 * hablando de la misma mercaderia con dos vocabularios distintos: Catalogo
 * decia «Accesorios», que es como se le dice en el local, e Inventario decia
 * «Cables y adaptadores», que es como se llama la familia en la base. El mismo
 * producto, dos nombres, segun la pantalla.
 *
 * La seccion es del negocio, no de una ruta. Por eso esta aca.
 *
 * UNA SECCION ES UN PUÑADO DE FAMILIAS
 * ------------------------------------
 * «Accesorios» son ocho rubros de la base; «Perfumes» es uno solo. El mapa se
 * ajusta editando `RUBROS_POR_SECCION`, sin tocar datos: lo que no figura —y no
 * es iPhone sellado ni usado— cae en «Otros equipos», que existe para que nada
 * quede invisible.
 *
 * DOS EXCEPCIONES QUE NO SALEN DEL RUBRO
 * --------------------------------------
 *   · Un iPhone SELLADO se reconoce por el nombre, no por la familia: comparte
 *     el rubro «Apple» con los Mac y los Watch.
 *   · Un iPHONE USADO se reconoce por `es_usado` mas el nombre. Antes bastaba
 *     `es_usado` —y antes de eso ni eso: un usado abierto desde el buscador
 *     global aterrizaba en «Otros equipos», con la ficha correcta arriba y la
 *     solapa equivocada de fondo—.
 *
 * «USADOS» ES LA SECCION DE LOS iPHONE USADOS
 * -------------------------------------------
 * No es la de todo lo de segunda mano, y no podia serlo: la solapa esta armada
 * sobre el lineup de Apple —una fila por modelo y memoria, los colores que
 * Apple hizo, las unidades colgadas de su renglon— y un Samsung no tiene nada
 * de eso. Cuando `es_usado` alcanzaba para entrar, el unico Samsung usado del
 * local quedaba solo en una seccion que no sabia dibujarlo, y no aparecia con
 * los otros telefonos, que es donde lo busca el que pregunta por uno.
 *
 * Un usado que no es iPhone cae ahora en la familia de lo que ES —un Samsung en
 * «Celulares», al lado del Redmi nuevo, en la solapa «Otras marcas»— sin dejar
 * de ser usado: conserva su IMEI, su bateria, su garantia, su ficha y su
 * asistente de edicion.
 */
import { esIphonePorNombre, esIphoneSellado } from "@/components/iphones/logica";
import { type FiltrosStock, type Producto, type Rubro } from "@/lib/tipos";

export const SECCIONES = [
  { clave: "iphone", label: "Sellados" },
  { clave: "usados", label: "Usados" },
  { clave: "marcas", label: "Otras marcas" },
  { clave: "apple", label: "Apple" },
  { clave: "accesorios", label: "Accesorios" },
  { clave: "perfumes", label: "Perfumes" },
  { clave: "vapers", label: "Vapers" },
  { clave: "otros", label: "Otros equipos" },
  { clave: "apedido", label: "A pedido" },
] as const;

export type Seccion = (typeof SECCIONES)[number]["clave"];

/**
 * Que familias de la base componen cada seccion.
 *
 * `iphone` y `usados` no figuran: no se definen por familia (ver el encabezado).
 *
 * Apple entra con sus cinco familias (0041). La seccion sigue mostrando todo
 * junto —son 36 productos, no hace falta partirla— pero el filtro de familia
 * sirve para ir derecho a los Mac o a los Watch. «Apple» a secas queda como la
 * quinta opcion: es lo original suelto, el AirTag, los cargadores, el Magic
 * Mouse y los Pencil.
 *
 * Accesorios entra con sus cinco familias (0043) mas los rubros de marca y de
 * simil que ya tenia. Son ocho opciones, todas con productos: «Accesorios» a
 * secas queda ultimo y es la cola —smartwatch genericos, memoria, bazar— que
 * no entra en ninguna.
 *
 * «Otras marcas» son los telefonos que no son iPhone, nuevos y usados, al lado
 * de Sellados y Usados: el que entra preguntando por un celular los busca ahi.
 * Un Samsung que se recibe en parte de pago cae en «Celulares» (ver
 * `rubroDeUsado`) y entra solo. «Usados» tambien figura: los iPhone usados se
 * separan antes por nombre, asi que lo que queda en esa familia es un telefono
 * de otra marca con la ficha vieja —de antes de 0084—.
 *
 * «Otros equipos» se queda con el resto de los equipos: tablets, relojes,
 * consolas y camaras.
 */
export const RUBROS_POR_SECCION: Partial<Record<Seccion, string[]>> = {
  apple: ["Apple", "Mac", "iPad", "Watch", "AirPods"],
  accesorios: [
    "Accesorios",
    "Cables y adaptadores",
    "Cargadores y baterías",
    "Audio",
    "Fundas y protección",
    "Soportes",
    "Simil Stanley",
    "Hogar",
  ],
  marcas: ["Celulares", "Usados"],
  otros: ["Tablets", "Smartwatch", "Consolas y Cámaras"],
  perfumes: ["Arabian Scents"],
  vapers: ["Californian Vapes"],
};

/** id de familia → a que seccion pertenece. Lo que no cae en ninguna, a «Otros». */
export function mapaRubroASeccion(rubros: ReadonlyArray<Rubro>): Map<number, Seccion> {
  const mapa = new Map<number, Seccion>();
  for (const r of rubros) {
    const duenia = (Object.keys(RUBROS_POR_SECCION) as Seccion[]).find((v) =>
      RUBROS_POR_SECCION[v]?.includes(r.nombre),
    );
    mapa.set(r.id, duenia ?? "otros");
  }
  return mapa;
}

/**
 * Un equipo de la seccion «Usados»: es usado Y es un iPhone.
 *
 * Los dos datos hacen falta. Sin `es_usado`, un sellado entraria; sin el
 * nombre, entraria el Samsung que se recibio en parte de pago, que es usado
 * pero se busca en «Otras marcas».
 */
export function esUsadoIphone(p: Producto): boolean {
  return p.es_usado && esIphonePorNombre(p.nombre);
}

/** En que seccion del mostrador se busca este producto. */
export function seccionDe(p: Producto, porRubro: Map<number, Seccion>): Seccion {
  if (esIphoneSellado(p)) return "iphone";
  if (esUsadoIphone(p)) return "usados";
  return (p.rubro_id !== null && porRubro.get(p.rubro_id)) || "otros";
}

/**
 * Si Sellados y Usados agregan el lineup entero (los modelos y memorias sin
 * cargar). Con un filtro puesto o algo escrito en el buscador, no: la persona
 * busca algo puntual, y un renglón que no existe no coincide con nada. Antes el
 * texto no contaba, y «zzzz» seguía mostrando todos los modelos.
 */
export function mostrarLineup({
  soloConStock,
  soloIncompletos,
  texto,
}: {
  soloConStock: boolean;
  soloIncompletos: boolean;
  texto: string;
}): boolean {
  return !soloConStock && !soloIncompletos && texto.trim() === "";
}

/**
 * Cuantos hay en cada seccion, para las solapas.
 *
 * «A pedido» se cuenta aparte porque `seccionDe` nunca la devuelve: es una
 * marca, no una familia, y sus filas viven ademas en su seccion de siempre. La
 * solapa decia «A pedido | 0» con trece productos adentro, porque el contador
 * quedaba fijo en cero mientras la lista se armaba con `a_pedido`.
 */
export function conteosDeSecciones(
  catalogo: ReadonlyArray<Producto>,
  usadosIphone: ReadonlyArray<Producto>,
  aPedido: ReadonlyArray<Producto>,
  porRubro: Map<number, Seccion>,
): Record<Seccion, number> {
  const acc: Record<Seccion, number> = {
    iphone: 0,
    apple: 0,
    // El contador de la solapa cuenta lo mismo que la lista: los iPhone
    // usados, sin los vendidos.
    usados: usadosIphone.length,
    marcas: 0,
    accesorios: 0,
    perfumes: 0,
    vapers: 0,
    otros: 0,
    apedido: aPedido.length,
  };
  for (const p of catalogo) acc[seccionDe(p, porRubro)] += 1;
  return acc;
}

/**
 * Los nombres de familia que le tocan a una seccion, para filtrar en el
 * servidor. Vacio = la seccion no se define por familia y hay que filtrar de
 * otra forma (o no filtrar).
 */
export function rubrosDeSeccion(seccion: Seccion | ""): string[] {
  if (seccion === "") return [];
  // «A pedido» no tiene familia: se define por la marca `a_pedido`, que puede
  // llevar cualquier producto. Hubo un rubro con ese nombre y se dio de baja
  // (0099); cae en el `?? []` de abajo como Sellados y Usados.
  return RUBROS_POR_SECCION[seccion] ?? [];
}

/**
 * A que seccion pertenece una fila del desglose por familia.
 *
 * Es `seccionDe` para un agregado en vez de para un producto: la fila trae la
 * familia y las dos banderas, que es todo lo que la regla necesita. El orden de
 * las preguntas es el mismo, y tiene que serlo — un iPhone sellado vive en la
 * familia «Apple» y si se preguntara por la familia primero caeria ahi.
 */
export function seccionDeFamilia(fila: {
  rubro: string;
  a_pedido: boolean;
  es_iphone_sellado: boolean;
  es_usado_iphone: boolean;
}): Seccion {
  // «A pedido» primero y aparte: no esta en el local, asi que no es plata que
  // haya en ninguna seccion. Si se contara dentro de Usados o de Sellados, el
  // desglose diria que hay mercaderia donde no hay ninguna. La vista ya manda
  // `a_pedido` solo para lo que esta en cero o en negativo (0125): un equipo
  // con la marca y unidades en la vitrina cuenta en su seccion.
  if (fila.a_pedido) return "apedido";
  if (fila.es_iphone_sellado) return "iphone";
  // `es_usado_iphone` y no `es_usado`: la plata de un Samsung usado se cuenta
  // en «Otras marcas», que es la seccion donde ese equipo se ofrece. Contarla
  // en «Usados» pondria plata en una solapa que no lo lista.
  if (fila.es_usado_iphone) return "usados";
  const duenia = (Object.keys(RUBROS_POR_SECCION) as Seccion[]).find((v) =>
    RUBROS_POR_SECCION[v]?.includes(fila.rubro),
  );
  return duenia ?? "otros";
}

/**
 * La seccion del mostrador, traducida a lo que entiende la consulta de stock.
 *
 * SE FILTRA EN EL SERVIDOR Y NO EN LA PANTALLA. Recortar las filas despues de
 * traerlas haria mentir a las dos cosas que cuentan: el pie diria "12 de 367"
 * cuando en esa seccion hay 40, y «Ver mas» traeria paginas que ya no aportan.
 *
 * EL ORDEN DE LAS PREGUNTAS ES EL MISMO QUE EN `seccionDe`, y tiene que serlo:
 * un iPhone sellado comparte la familia «Apple» con los Mac, asi que la seccion
 * Apple tiene que dejarlo afuera explicitamente o el mismo equipo apareceria en
 * las dos.
 *
 * `rubro` es la familia ya elegida adentro de la seccion, para afinar. Sin
 * seccion no se traduce nada: la familia filtra sola, como siempre.
 */
export function filtrosDeSeccion(seccion: Seccion | "", rubro = ""): Partial<FiltrosStock> {
  if (seccion === "") return rubro ? { rubro } : {};

  // «A pedido» se pregunta por la marca de la ficha y no por la familia: puede
  // llevarla cualquier cosa, sellada o usada, de cualquier rubro.
  if (seccion === "apedido") return { a_pedido: true };
  if (seccion === "iphone") return { es_iphone_sellado: true, a_pedido: false };
  if (seccion === "usados") return { es_usado_iphone: true, a_pedido: false };

  // Lo que se deja afuera son los EQUIPOS de las otras dos solapas, no los
  // usados: un Samsung usado vive en «Celulares» y esta seccion es la suya. Por
  // eso se pregunta por `es_usado_iphone` y no por `es_usado`, que lo sacaria
  // de la unica seccion que lo muestra.
  return {
    rubro: rubro ? rubro : rubrosDeSeccion(seccion).join(","),
    es_usado_iphone: false,
    es_iphone_sellado: false,
    a_pedido: false,
  };
}
